/**
 * User Context
 * Manages user identity and user-specific settings in the Flex Scheduler app
 * 
 * For embedded Shopify apps, user identity comes from App Bridge session.
 * For standalone development, a mock user is used.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase, getShopDomain } from '../utils/supabase';
import { getToken, isEmbedded } from '../utils/authenticated-fetch';

// User preferences that can be customized per-user
export interface UserPreferences {
  timezone: string;
  enableEmailNotifications: boolean;
  notificationEmail?: string;
  notificationEmails?: string[];  // Multiple email addresses for notifications
  notifyOnCompleted: boolean;
  notifyOnFailed: boolean;
  notifyOnPending: boolean;
  defaultView: 'list' | 'calendar';
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  showCompletedSchedules: boolean;
  autoExpandDetails: boolean;
  showCancelledSchedules?: boolean;
}

// Default user preferences
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  timezone: 'America/New_York',
  enableEmailNotifications: false,
  notificationEmails: [],
  notifyOnCompleted: true,
  notifyOnFailed: true,
  notifyOnPending: false,
  defaultView: 'list',
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  showCompletedSchedules: true,
  autoExpandDetails: false,
  showCancelledSchedules: false,
};

// User information
export interface UserInfo {
  id: string;
  email?: string;
  name?: string;
  isOwner?: boolean;
}

// Context value
interface UserContextValue {
  user: UserInfo | null;
  preferences: UserPreferences;
  isLoading: boolean;
  isAuthenticated: boolean;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  saveAccountProfile: (profile: { name: string; timezone: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

// Development mock user
const DEV_USER: UserInfo = {
  id: 'dev-user-1',
  email: 'developer@localhost',
  name: 'Developer',
  isOwner: true,
};

interface UserProviderProps {
  children: ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_USER_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from Shopify session or use dev user
  const loadUser = useCallback(async () => {
    setIsLoading(true);
    
    try {
      let userInfo: UserInfo;
      
      if (isEmbedded()) {
        // In embedded mode, get user from App Bridge session
        try {
          const sessionToken = await getToken();
          if (sessionToken) {
            // Decode the JWT to get user info (don't verify, just extract)
            const payload = JSON.parse(atob(sessionToken.split('.')[1]));
            userInfo = {
              id: payload.sub || payload.dest?.split('/').pop() || 'unknown',
              email: payload.email,
              name: payload.name,
              isOwner: payload.owner === true,
            };
          } else {
            // Fallback to dev user if no session token
            userInfo = DEV_USER;
          }
        } catch (e) {
          console.warn('[UserContext] Failed to get session token:', e);
          userInfo = DEV_USER;
        }
      } else {
        // Standalone mode - use dev user
        userInfo = DEV_USER;
      }
      
      const hydratedUser = await loadPreferences(userInfo);
      setUser(hydratedUser);
      
    } catch (error) {
      console.error('[UserContext] Error loading user:', error);
      setUser(DEV_USER);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load preferences from Supabase
  const loadPreferences = async (userInfo: UserInfo): Promise<UserInfo> => {
    try {
      const shopDomain = getShopDomain();
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('preferences, user_email, user_name')
        .eq('shop_domain', shopDomain)
        .eq('user_id', userInfo.id)
        .maybeSingle();

      if (error) {
        console.warn('[UserContext] Error loading preferences:', error);
        return userInfo;
      }

      if (data && data.preferences) {
        setPreferences({
          ...DEFAULT_USER_PREFERENCES,
          ...data.preferences,
        });
      } else {
        // No preferences row exists for this user/shop yet — keep defaults
        console.debug('[UserContext] No user preferences found; using defaults');
      }

      return {
        ...userInfo,
        email: data?.user_email || userInfo.email,
        name: data?.user_name || userInfo.name,
      };
    } catch (error) {
      console.error('[UserContext] Error loading preferences:', error);
      return userInfo;
    }
  };

  // Update user preferences
  const updatePreferences = useCallback(async (updates: Partial<UserPreferences>) => {
    if (!user) return;

    const newPreferences = { ...preferences, ...updates };
    setPreferences(newPreferences);

    try {
      const shopDomain = getShopDomain();
      
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          shop_domain: shopDomain,
          user_id: user.id,
          user_email: user.email,
          user_name: user.name,
          preferences: newPreferences,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'shop_domain,user_id',
        });

      if (error) {
        console.error('[UserContext] Error saving preferences:', error);
        // Revert on error
        setPreferences(preferences);
        throw error;
      }
    } catch (error) {
      console.error('[UserContext] Error updating preferences:', error);
      throw error;
    }
  }, [user, preferences]);

  const saveAccountProfile = useCallback(async (profile: { name: string; timezone: string }) => {
    if (!user) return;

    const trimmedName = profile.name.trim();
    if (!trimmedName) {
      throw new Error('Your name is required.');
    }

    const nextPreferences = { ...preferences, timezone: profile.timezone };
    const previousUser = user;
    const previousPreferences = preferences;

    setUser({
      ...user,
      name: trimmedName,
    });
    setPreferences(nextPreferences);

    try {
      const shopDomain = getShopDomain();

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          shop_domain: shopDomain,
          user_id: user.id,
          user_email: user.email,
          user_name: trimmedName,
          preferences: nextPreferences,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'shop_domain,user_id',
        });

      if (error) {
        throw error;
      }
    } catch (error) {
      setUser(previousUser);
      setPreferences(previousPreferences);
      console.error('[UserContext] Error saving account profile:', error);
      throw error;
    }
  }, [preferences, user]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  // Initial load
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const value: UserContextValue = {
    user,
    preferences,
    isLoading,
    isAuthenticated: user !== null,
    updatePreferences,
    saveAccountProfile,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// Hook to access user context
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

// Hook to access just user preferences
export function useUserPreferences() {
  const { preferences, updatePreferences, isLoading } = useUser();
  return { preferences, updatePreferences, isLoading };
}

export default UserContext;
